/**
 * <bb-search-toolbar> — filter drawer and sort select of the search results page (bb-search section).
 *
 * The toolbar wraps one GET form (q, type, options[prefix], sort_by and every facet input). This element only
 * adds behaviour on top of it:
 *   - [data-bb-filters-open] opens the filters <dialog> as a modal drawer, [data-bb-filters-close] or a click
 *     on the backdrop closes it (Escape is handled natively by <dialog>).
 *   - Changing [data-bb-sort] submits the form, so the new order keeps the query and the applied filters.
 *
 * Applying filters is a plain form submit (full page load), so there is no client-side state to keep in sync.
 */
class BBSearchToolbar extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
  }

  /** @returns {HTMLDialogElement | null} */
  #dialog() {
    const dialog = this.querySelector('[data-bb-filters-dialog]');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const dialog = this.#dialog();
    if (!dialog) return;

    if (target.closest('[data-bb-filters-open]')) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      return;
    }

    // The backdrop belongs to the dialog element itself; the panel content fills the rest of it
    if (target.closest('[data-bb-filters-close]') || target === dialog) {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.hasAttribute('data-bb-sort')) return;
    const form = select.form;
    if (!form) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
  };
}

if (!customElements.get('bb-search-toolbar')) {
  customElements.define('bb-search-toolbar', BBSearchToolbar);
}
